import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { retry } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ResponsibleApi } from 'src/app/shared/apis/responsible.api';
import { CountdownLimitComponent } from 'src/app/shared/components/countdown-limit/countdown-limit.component';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSSubTabComponent } from 'src/app/shared/components/cs-sub-tab/cs-sub-tab.component';
import { CSTabComponent } from 'src/app/shared/components/cs-tab/cs-tab.component';
import { LoadingModule } from 'src/app/shared/directive/loading/loading.module';
import { UserAssetsService } from '../../user-asset/user-assets.service';
import { WalletDialogType, WalletService } from '../wallet.service';
import { DepositCryptoPiqComponent } from './deposit-crypto-piq/deposit-crypto-piq.component';
import { DepositCryptoComponent } from './deposit-crypto/deposit-crypto.component';
import { DepositTransferComponent } from './deposit-transfer/deposit-transfer.component';
import { DepositWeb3WalletComponent } from './deposit-web3-wallet/deposit-web3-wallet.component';
import { WithdrawTransferComponent } from './withdraw-transfer/withdraw-transfer.component';
import { WithdrawWeb3WalletComponent } from './withdraw-web3-wallet/withdraw-web3-wallet.component';

// wallet.types.ts
export type WalletTab = 'deposit' | 'withdraw';
export type SubTab = 'web3Wallet' | 'transfer' | 'buyCrypto';

export interface SubTabConfig {
  key: SubTab;
  i18nKey: string;
}

export interface TabConfig {
  key: WalletTab;
  i18nKey: string;
  subTabs: SubTabConfig[];
}

@Component({
  standalone: true,
  selector: 'deposit-withdraw',
  templateUrl: './deposit-withdraw.component.html',
  styleUrls: ['./deposit-withdraw.component.scss'],
  imports: [
    CSTabComponent,
    CSSubTabComponent,
    DepositWeb3WalletComponent,
    DepositTransferComponent,
    WithdrawWeb3WalletComponent,
    WithdrawTransferComponent,
    DepositCryptoComponent,
    DepositCryptoPiqComponent,
    CSButtonComponent,
    CommonModule,
    LoadingModule,
    CountdownLimitComponent,
  ],
})
export class DepositWithdrawComponent implements OnInit {
  private readonly baseTabsConfig: TabConfig[] = [
    {
      key: 'deposit',
      i18nKey: 'Deposit',
      subTabs: [
        { key: 'web3Wallet', i18nKey: 'Web3 Wallet' },
        { key: 'transfer', i18nKey: 'Transfer' },
        { key: 'buyCrypto', i18nKey: 'Buy Crypto' },
      ],
    },
    {
      key: 'withdraw',
      i18nKey: 'Withdraw',
      subTabs: [
        { key: 'web3Wallet', i18nKey: 'Web3 Wallet' },
        { key: 'transfer', i18nKey: 'Transfer' },
      ],
    },
  ];

  constructor(
    public walletService: WalletService,
    private route: ActivatedRoute,
    private router: Router,
    private appService: AppService,
    private userAssetsService: UserAssetsService,
    private responsibleApi: ResponsibleApi,
    private dialogRef: MatDialogRef<DepositWithdrawComponent>,
  ) {}

  /**
   * 是否切回 Rillpay (Key: rillpayEnabled)
   * Buy Crypto 預設 PaymentMethod 為PIQ
   * @returns boolean
   */
  get rillPayEnabled(): boolean {
    return this.appService?.tenantConfig$.value?.config?.rillpayEnabled === 'true' || false;
  }

  isLoading = false;
  dpLimitData: any;
  fetchDpLimit() {
    this.isLoading = true;
    this.responsibleApi
      .checkdepositlimit()
      .pipe(retry(3))
      .subscribe((res: any) => {
        this.isLoading = false;
        if (res.success) {
          this.dpLimitData = res.data;
          this.userAssetsService.dpRemainingAmount$.next(res.data.remainingAmount);
        }
      });
  }

  onCountdownComplete() {
    this.fetchDpLimit();
  }

  onSettings() {
    this.dialogRef.close();
    this.router.navigate([this.appService.languageCode, 'settings', 'responsible-gaming']);
  }

  tabsConfig: TabConfig[] = this.buildTabsConfig();

  tabIndex = 0;
  subTabIndex = 0;
  private lastTabParam: WalletTab = 'deposit';
  private lastSubTabParam?: SubTab;

  // 取出第一層 tab 的 key
  get tabKeys(): string[] {
    return this.tabsConfig.map(t => t.i18nKey);
  }

  // 取出對應 subTab 的 key
  get subTabKeys(): string[] {
    return this.tabsConfig[this.tabIndex]?.subTabs.map(s => s.i18nKey) ?? [];
  }

  // 當前tab
  get tab(): WalletTab {
    return this.tabsConfig[this.tabIndex].key;
  }

  // 當前subTabs群組
  get availableSubTabs(): SubTabConfig[] {
    return this.tabsConfig[this.tabIndex]?.subTabs ?? [];
  }

  // 當前subTab
  get subTab(): SubTab {
    return this.availableSubTabs[this.subTabIndex]?.key ?? this.availableSubTabs[0]?.key ?? 'web3Wallet';
  }

  // tab改變
  onTabChanged(index: number) {
    this.tabIndex = index;
    this.subTabIndex = 0; // reset to first sub tab
    this.updateUrl();
  }

  // subTab改變
  onSubTabChanged(index: number) {
    this.subTabIndex = index;
    this.updateUrl();
  }

  // 回到初始畫面
  back() {
    this.walletService.walletDialogType$.next(WalletDialogType.FIRST_DEPOSIT);
  }

  ngOnInit() {
    this.fetchDpLimit();
    this.applyTabFromParams();
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'] === 'withdraw' ? 'withdraw' : 'deposit';
      const subTab = params['subTab'] as SubTab | undefined;

      this.lastTabParam = tab;
      this.lastSubTabParam = subTab;
      this.applyTabFromParams();

      if (tab && subTab) {
        this.walletService.walletDialogType$.next(WalletDialogType.DEPOSIT_WITHDRAW);
      }

      this.walletService.targetCurrency = params['targetCurrency'] || '';
      this.walletService.bonusId = params['bonusId'] || '';
    });
  }

  private buildTabsConfig(): TabConfig[] {
    const tabs = this.baseTabsConfig.map(tab => ({
      ...tab,
      subTabs: [...tab.subTabs],
    }));
    return tabs;
  }

  private applyTabFromParams() {
    const tabIdx = this.tabsConfig.findIndex(t => t.key === this.lastTabParam);
    this.tabIndex = tabIdx !== -1 ? tabIdx : 0;

    const subIdx = this.availableSubTabs.findIndex(t => t.key === this.lastSubTabParam);
    this.subTabIndex = subIdx !== -1 ? subIdx : 0;
  }

  updateUrl() {
    this.router.navigate([], {
      queryParams: {
        tab: this.tab,
        subTab: this.subTab,
        dialog: 'wallet',
      },
      queryParamsHandling: 'merge',
    });
  }
}
